import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import './apply.css';

const Apply = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { position = '', company = '' } = location.state || {};

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    idNumber: '',
    position: position,
    company: company,
    experience: '',
    education: '',
    skills: '',
    coverLetter: '',
    salaryExpectation: '',
    noticePeriod: '',
    referral: '',
    cv: null
  });

  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'cv') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'cv' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Send to Formspree
      const response = await fetch('https://formspree.io/f/xgvpadav', {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitted(true);
        // Also save as PDF
        generatePDF();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Application failed. Please try again or email us directly.');
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Add styling
    doc.setFillColor(38, 47, 217);
    doc.rect(0, 0, 210, 40, 'F');
    
    // Header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ENERSTIC APPLICATION', 105, 20, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text('Application Summary', 20, 50);
    
    // Personal Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Personal Information', 20, 65);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yPosition = 75;
    
    const fields = [
      { label: 'Name:', value: `${formData.firstName} ${formData.lastName}` },
      { label: 'Email:', value: formData.email },
      { label: 'Phone:', value: formData.phone },
      { label: 'ID Number:', value: formData.idNumber },
      { label: 'Position:', value: formData.position },
      { label: 'Company:', value: formData.company },
      { label: 'Salary Expectation:', value: formData.salaryExpectation },
      { label: 'Notice Period:', value: formData.noticePeriod }
    ];

    fields.forEach(field => {
      if (field.value) {
        doc.text(`${field.label} ${field.value}`, 20, yPosition);
        yPosition += 7;
      }
    });

    // Experience
    if (formData.experience) {
      yPosition += 10;
      doc.setFont('helvetica', 'bold');
      doc.text('Work Experience', 20, yPosition);
      yPosition += 7;
      doc.setFont('helvetica', 'normal');
      const experienceLines = doc.splitTextToSize(formData.experience, 170);
      doc.text(experienceLines, 20, yPosition);
      yPosition += (experienceLines.length * 5) + 5;
    }

    // Education
    if (formData.education) {
      doc.setFont('helvetica', 'bold');
      doc.text('Education', 20, yPosition);
      yPosition += 7;
      doc.setFont('helvetica', 'normal');
      const educationLines = doc.splitTextToSize(formData.education, 170);
      doc.text(educationLines, 20, yPosition);
      yPosition += (educationLines.length * 5) + 5;
    }

    // Skills
    if (formData.skills) {
      doc.setFont('helvetica', 'bold');
      doc.text('Skills', 20, yPosition);
      yPosition += 7;
      doc.setFont('helvetica', 'normal');
      const skillsLines = doc.splitTextToSize(formData.skills, 170);
      doc.text(skillsLines, 20, yPosition);
      yPosition += (skillsLines.length * 5) + 5;
    }

    // Cover Letter
    if (formData.coverLetter) {
      doc.setFont('helvetica', 'bold');
      doc.text('Cover Letter', 20, yPosition);
      yPosition += 7;
      doc.setFont('helvetica', 'normal');
      const coverLetterLines = doc.splitTextToSize(formData.coverLetter, 170);
      doc.text(coverLetterLines, 20, yPosition);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Application generated on: ${new Date().toLocaleDateString()}`, 20, 280);

    // Save the PDF
    const fileName = `enerstic-application-${formData.firstName}-${formData.lastName}.pdf`;
    doc.save(fileName);
  };

  const saveAsPDFOnly = () => {
    generatePDF();
    alert('PDF saved successfully!');
  };

  const goBack = () => {
    navigate(-1); // Go back to opportunities page
  };

  if (submitted) {
    return (
      <div className="apply-page">
        <div className="apply-success">
          <div className="success-icon">✅</div>
          <h1>Application Submitted!</h1>
          <p className="success-message">
            Thank you for applying to {formData.position} at {formData.company}. 
            We've received your application and saved a PDF copy for your records.
          </p>
          <p className="success-note">
            Our HR team will review your application and contact you if you're shortlisted.
          </p>
          <div className="success-actions">
            <button onClick={saveAsPDFOnly} className="pdf-btn">
              📄 Download PDF Copy
            </button>
            <button onClick={goBack} className="back-btn">
              🔍 View More Opportunities
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="apply-page">
      <div className="apply-header">
        <button onClick={goBack} className="back-button">← Back</button>
        <h1>Job Application</h1>
        <p>Apply for {position} at {company}</p>
      </div>

      <form onSubmit={handleSubmit} className="apply-form">
        <div className="form-section">
          <h3>👤 Personal Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ID Number *</label>
              <input
                type="text"
                name="idNumber"
                value={formData.idNumber}
                onChange={handleInputChange}
                required
                placeholder="South African ID Number"
              />
            </div>
            <div className="form-group">
              <label>Upload CV (PDF/DOC) *</label>
              <input
                type="file"
                name="cv"
                onChange={handleInputChange}
                accept=".pdf,.doc,.docx"
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>💼 Position Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Position Applied For *</label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Company *</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Salary Expectation (ZAR)</label>
              <input
                type="text"
                name="salaryExpectation"
                value={formData.salaryExpectation}
                onChange={handleInputChange}
                placeholder="e.g., R25,000 - R35,000 pm"
              />
            </div>
            <div className="form-group">
              <label>Notice Period</label>
              <select
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={handleInputChange}
              >
                <option value="">Select notice period</option>
                <option value="Immediately">Immediately</option>
                <option value="1 week">1 week</option>
                <option value="2 weeks">2 weeks</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>🎓 Experience & Qualifications</h3>
          <div className="form-group">
            <label>Work Experience *</label>
            <textarea
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              placeholder="Describe your relevant work experience, including company names, positions, and duration..."
              rows="4"
              required
            />
          </div>

          <div className="form-group">
            <label>Education & Qualifications *</label>
            <textarea
              name="education"
              value={formData.education}
              onChange={handleInputChange}
              placeholder="List your educational background, degrees, diplomas, and certifications..."
              rows="4"
              required
            />
          </div>

          <div className="form-group">
            <label>Skills & Competencies *</label>
            <textarea
              name="skills"
              value={formData.skills}
              onChange={handleInputChange}
              placeholder="List your key skills, technical competencies, and languages spoken..."
              rows="3"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>📝 Cover Letter</h3>
          <div className="form-group">
            <label>Why are you interested in this position? *</label>
            <textarea
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleInputChange}
              placeholder="Tell us why you're the right candidate for this position and what you can bring to our team..."
              rows="6"
              required
            />
          </div>

          <div className="form-group">
            <label>How did you hear about this opportunity?</label>
            <input
              type="text"
              name="referral"
              value={formData.referral}
              onChange={handleInputChange}
              placeholder="e.g., Enerstic website, LinkedIn, referral, etc."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            📨 Submit Application & Save PDF
          </button>
          <button type="button" onClick={saveAsPDFOnly} className="pdf-only-btn">
            💾 Save as PDF Only
          </button>
        </div>

        <div className="form-note">
          <p>📋 <strong>Note:</strong> Your application will be submitted to our HR team and a PDF copy will be automatically saved for your records.</p>
        </div>
      </form>
    </div>
  );
};

export default Apply;